// dynamicFieldKeys.ts
import type { Editor } from '@tiptap/core';

/**
 * Adapted from the old `smartFieldKeys.ts` (SmartFieldNode era) — the
 * slugify/disambiguate logic was never the broken part, only the Node-based
 * `collectExistingFieldKeys` walk was, since a Node has no `marks` array to
 * search. Rewritten here to walk text nodes carrying the `dynamicField`
 * Mark instead, the same way `dynamicFieldSync.ts` does.
 *
 * Keys are template-scoped, not globally unique — a key only needs to be
 * unique within the one template/editor instance it lives in.
 */
export function slugifyFieldKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function collectExistingDynamicFieldKeys(editor: Editor): Set<string> {
  const keys = new Set<string>();
  if (!editor) {
    return keys;
  }
  editor.state.doc.descendants(node => {
    if (!node.isText) {
      return;
    }
    const mark = node.marks.find(m => m.type.name === 'dynamicField');
    const key = typeof mark?.attrs.class === 'string' ? mark.attrs.class.trim().split(/\s+/)[0] : null;
    if (key) {
      keys.add(key);
    }
  });
  return keys;
}

/**
 * Slugify `label` into a fieldKey, disambiguating against every dynamic-field
 * key already present in this editor's document with a `-2`, `-3`, … suffix.
 * Falls back to `field` if the label slugifies to nothing (e.g. all
 * punctuation) so a key is never blank.
 */
export function generateUniqueDynamicFieldKey(editor: Editor, label: string): string {
  const base = slugifyFieldKey(label) || 'field';
  const existing = collectExistingDynamicFieldKeys(editor);
  if (!existing.has(base)) {
    return base;
  }
  let n = 2;
  while (existing.has(`${base}-${n}`)) {
    n += 1;
  }
  return `${base}-${n}`;
}
